import Money from "@dashboard/components/Money";
import { DashboardModal } from "@dashboard/components/Modal";
import {
  GetQuotationCheckoutDetailsQuery,
  useUpdateQuotePricingMutation,
  useUpdateMetadataMutation,
} from "@dashboard/graphql";
import { Button } from "@saleor/macaw-ui";
import { Box, Divider, Skeleton, Text } from "@saleor/macaw-ui-next";
import { TextField } from "@material-ui/core";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import useNotifier from "@dashboard/hooks/useNotifier";
import { useUser } from "@dashboard/auth";

interface QuotationDetailsDialogProps {
  open: boolean;
  loading: boolean;
  checkout?: GetQuotationCheckoutDetailsQuery["checkout"] | null;
  onClose: () => void;
}

const metadataFields = [
  { key: "customMessage", defaultMessage: "Message" },
  { key: "customQuantity", defaultMessage: "Custom Quantity" },
  { key: "customSets", defaultMessage: "Custom Sets" },
  { key: "customSize", defaultMessage: "Custom Size" },
] as const;

const nonVendorMetadataFields = [
  { key: "Production Cost", label: "Production Cost" },
  { key: "Shipping Weight", label: "Shipping Weight" },
  { key: "Sale", label: "Sale" },
  { key: "Shipping Cost", label: "Shipping Cost" },
] as const;

const QuotationDetailsDialog: React.FC<QuotationDetailsDialogProps> = ({
  open,
  loading,
  checkout,
  onClose,
}) => {
  const intl = useIntl();
  const { user } = useUser();
  const notify = useNotifier();
  const [updateQuotePricing] = useUpdateQuotePricingMutation();
  const [updateMetadata, { loading: updatingMetadata }] = useUpdateMetadataMutation();
  const [priceValues, setPriceValues] = React.useState<Record<string, string>>({});
  const [updatingLineId, setUpdatingLineId] = React.useState<string | null>(null);
  const isVendor = !!user?.isVendor;
  const [metadataValues, setMetadataValues] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!isVendor && checkout?.metadata) {
      const initialValues: Record<string, string> = {};
      checkout.metadata.forEach(item => {
        if (item.key) {
          initialValues[item.key] = item.value || "";
        }
      });
      setMetadataValues(initialValues);
    } else {
      setMetadataValues({});
    }
  }, [checkout?.id, checkout?.metadata, isVendor]);

  const thicknessLine = React.useMemo(() => {
    if (!isVendor || !checkout?.lines) {
      return null;
    }

    const byMetadata = checkout.lines.find(line =>
      line?.metadata?.some(meta => meta?.key === "thickness"),
    );

    return byMetadata ?? checkout.lines[0] ?? null;
  }, [checkout?.lines, isVendor]);

  React.useEffect(() => {
    if (isVendor && thicknessLine?.id) {
      const thicknessValue = thicknessLine.totalPrice?.gross?.amount?.toString() || "";
      setPriceValues({
        [thicknessLine.id]: thicknessValue,
      });
    } else {
      setPriceValues({});
    }
  }, [isVendor, thicknessLine]);

  const handlePriceChange = (lineId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPriceValues(prev => ({
      ...prev,
      [lineId]: value,
    }));
  };

  const handleMetadataChange = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setMetadataValues(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleUpdatePrice = async (lineId: string, quantity: number) => {
    if (!checkout?.id || !isVendor) {
      return;
    }

    const priceValue = priceValues[lineId];

    if (!priceValue || Number(priceValue) <= 0) {
      notify({
        status: "error",
        text: intl.formatMessage({
          id: "quotationInvalidPrice",
          defaultMessage: "Enter a value greater than zero.",
        }),
      });
      return;
    }

    setUpdatingLineId(lineId);
    try {
      const { data } = await updateQuotePricing({
        variables: {
          checkoutId: checkout.id,
          lineId,
          quantity,
          price: priceValue,
        },
      });

      const errors = data?.checkoutLinesUpdate?.errors ?? [];
      if (errors.length) {
        notify({
          status: "error",
          text: errors[0].message
            ? errors[0].message
            : intl.formatMessage({
                id: "quotationPriceUpdateError",
                defaultMessage: "Failed to update quote.",
              }),
        });
        return;
      }

      notify({
        status: "success",
        text: intl.formatMessage({
          id: "quotationPriceUpdated",
          defaultMessage: "Quote updated successfully.",
        }),
      });
      onClose();
    } catch {
      notify({
        status: "error",
        text: intl.formatMessage({
          id: "quotationPriceUpdateError",
          defaultMessage: "Failed to update quote.",
        }),
      });
    } finally {
      setUpdatingLineId(null);
    }
  };

  const handleSaveMetadata = async () => {
    if (!checkout?.id || isVendor) {
      return;
    }

    try {
      const metadataInput: Array<{ key: string; value: string }> = [];

      nonVendorMetadataFields.forEach(field => {
        const value = metadataValues[field.key] || "";
        if (value) {
          metadataInput.push({
            key: field.key,
            value,
          });
        }
      });

      if (metadataInput.length === 0) {
        notify({
          status: "error",
          text: intl.formatMessage({
            id: "quotationMetadataEmpty",
            defaultMessage: "Please fill at least one field.",
          }),
        });
        return;
      }

      const { data } = await updateMetadata({
        variables: {
          id: checkout.id,
          input: metadataInput,
          keysToDelete: [],
        },
      });

      const errors = data?.updateMetadata?.errors ?? [];
      if (errors.length > 0) {
        notify({
          status: "error",
          text: errors[0].message
            ? errors[0].message
            : intl.formatMessage({
                id: "quotationMetadataUpdateError",
                defaultMessage: "Failed to add details.",
              }),
        });
        return;
      }

      notify({
        status: "success",
        text: intl.formatMessage({
          id: "quotationMetadataUpdated",
          defaultMessage: "Details added successfully.",
        }),
      });
      onClose();
    } catch (error) {
      notify({
        status: "error",
        text: intl.formatMessage({
          id: "quotationMetadataUpdateError",
          defaultMessage: "Failed to add details.",
        }),
      });
    }
  };

  const firstLineMetadata = checkout?.lines?.[0]?.metadata || [];
  const metadataMap = Object.fromEntries(firstLineMetadata.map(item => [item.key, item.value]));
  const nonVendorMetaData = checkout?.metadata || [];
  const nonVendorMetaDataMap = Object.fromEntries(
    nonVendorMetaData?.map(item => [item.key, item.value]),
  );

  let customSizeDisplay = "-";
  if (metadataMap.customSize) {
    try {
      const sizeObj = JSON.parse(metadataMap.customSize);
      if (sizeObj.width && sizeObj.height) {
        customSizeDisplay = `${sizeObj.width} x ${sizeObj.height}`;
      }
    } catch {
      customSizeDisplay = metadataMap.customSize;
    }
  }

  return (
    <DashboardModal open={open} onChange={onClose}>
      <DashboardModal.Content size="md">
        <DashboardModal.Header>
          <DashboardModal.Title>
            <FormattedMessage id="quotationDetailsTitle" defaultMessage="Quotation Details" />
          </DashboardModal.Title>
        </DashboardModal.Header>

        <DashboardModal.Grid>
          {loading ? (
            <Skeleton __height="200px" />
          ) : checkout ? (
            <Box display="flex" flexDirection="column" gap={6}>
              <Box
                display="grid"
                gap={4}
                __gridTemplateColumns={["1fr", "repeat(2, minmax(0, 1fr))"]}
              >
                <Box>
                  <Text variant="bodyStrong">
                    <strong>Email:</strong>
                  </Text>
                  <Text marginLeft={1}>{checkout.email ?? "-"}</Text>
                </Box>
                <Box>
                  <Text variant="bodyStrong">
                    <strong>Total:</strong>
                  </Text>
                  <Text marginLeft={1}>
                    {checkout.totalPrice?.gross ? <Money money={checkout.totalPrice.gross} /> : "-"}
                  </Text>
                </Box>
              </Box>

              <Divider />

              <Box
                display="grid"
                gap={4}
                __gridTemplateColumns={["1fr", "repeat(2, minmax(0, 1fr))"]}
              >
                {metadataFields.map(field => (
                  <Box key={field.key}>
                    <Text variant="bodyStrong">
                      <strong>{field.defaultMessage}:</strong>
                    </Text>
                    <Text marginLeft={1}>
                      {field.key === "customSize"
                        ? customSizeDisplay
                        : (metadataMap[field.key] ?? "-")}
                    </Text>
                  </Box>
                ))}

                {isVendor &&
                  nonVendorMetadataFields?.map(field => (
                    <Box key={field.key}>
                      <Text variant="bodyStrong">
                        <strong>{field.label}:</strong>
                      </Text>
                      <Text marginLeft={1}>{nonVendorMetaDataMap[field.key] ?? "-"}</Text>
                    </Box>
                  ))}
              </Box>

              <Divider />

              {isVendor && thicknessLine && (
                <Box display="flex" flexDirection="column" gap={5}>
                  <Box display="flex" flexDirection="column" gap={4}>
                    <Box>
                      <Text variant="bodyStrong">{thicknessLine.variant?.name ?? "-"}</Text>
                    </Box>
                    <Box display="flex" gap={2}>
                      <TextField
                        type="number"
                        label={intl.formatMessage({
                          id: "quotationThicknessInputLabel",
                          defaultMessage: "Thickness",
                        })}
                        value={priceValues[thicknessLine.id] || ""}
                        onChange={handlePriceChange(thicknessLine.id)}
                        fullWidth
                        inputProps={{ min: 0, step: "0.01" }}
                      />
                      <Button
                        variant="primary"
                        onClick={() =>
                          handleUpdatePrice(thicknessLine.id, thicknessLine.quantity ?? 1)
                        }
                        disabled={
                          updatingLineId === thicknessLine.id ||
                          !priceValues[thicknessLine.id] ||
                          Number(priceValues[thicknessLine.id]) <= 0
                        }
                      >
                        <FormattedMessage id="quotationUpdateThickness" defaultMessage="Update" />
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}

              {!isVendor && (
                <Box display="flex" flexDirection="column" gap={4}>
                  <Text variant="bodyStrong">
                    <FormattedMessage
                      id="quotationMetadataFields"
                      defaultMessage="Add More Details"
                    />
                  </Text>
                  <Box
                    display="grid"
                    gap={4}
                    __gridTemplateColumns={["1fr", "repeat(2, minmax(0, 1fr))"]}
                  >
                    {nonVendorMetadataFields.map(field => (
                      <TextField
                        key={field.key}
                        type="number"
                        label={field.label}
                        value={metadataValues[field.key] || ""}
                        onChange={handleMetadataChange(field.key)}
                        fullWidth
                        inputProps={{ min: 0, step: "0.01" }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Text variant="bodyStrong">No checkout details available.</Text>
          )}
        </DashboardModal.Grid>
        <DashboardModal.Actions>
          <Button variant="secondary" onClick={onClose}>
            <FormattedMessage id="quotationDetailsClose" defaultMessage="Close" />
          </Button>
          {!isVendor && checkout && (
            <Button variant="primary" onClick={handleSaveMetadata} disabled={updatingMetadata}>
              <FormattedMessage id="quotationSaveMetadata" defaultMessage="Save" />
            </Button>
          )}
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

export default QuotationDetailsDialog;
